import React, { useState, useEffect, useCallback, useContext } from "react";
import MarketFeed from "./components/MarketFeed";
import AssetChart from "./components/AssetChart";
import OrderPanel from "./components/OrderPanel";
import AccountSummary from "./components/AccountSummary";
import styles from "./Arena.module.css";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import CommonButton from "../../ui/CommonButton";
import { MarketContext } from "../../context/MarketContext";
import useWindowDimensions from "../../hooks/getWindowDimensions";
import { env } from "../../constants/env";

const Overlay = ({ title, message, buttonText, onButtonClick }) => {
  return (
    <div className={styles.overlay}>
      <div className={styles.overlayContent}>
        <h5 className="text-white mb-2 text-center">{title}</h5>
        <p className="text-center mb-3">{message}</p>
        {buttonText && onButtonClick && (
          <CommonButton onClick={onButtonClick} text={buttonText} />
        )}
      </div>
    </div>
  );
};

const Arena = () => {
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const { inactive, setInactive } = useContext(MarketContext);

  const { height, width } = useWindowDimensions();

  const [arenaLocked, setArenaLocked] = useState(false);

  const shouldPause = arenaLocked || inactive;

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;

    const checkLocked = () => {
      fetch(`${env.apiUrl}/api/redeem/is-locked`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => setArenaLocked(data.locked))
        .catch(() => {});
    };

    checkLocked(); // initial load
    const interval = setInterval(checkLocked, 15000); // check every 15s

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setInactive(true), 3 * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timeout);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, []);

  const handleResume = useCallback(() => setInactive(false), [setInactive]);

  return (
    <div className={`container-fluid ${styles.app} position-relative`}>

      {/* <Overlay
          title="Back Soon!"
          message="The arena will be back Dec 4th 2025"
        /> */}
    
      {arenaLocked ? (
        <Overlay
          title="Max Reward Redeemed"
          message="You have redeemed a funded account reward. The Arena is now locked."
        />
      ) : inactive ? (
        <Overlay
          title="Chart updates paused"
          message="Updates have been paused due to inactivity. Click the button below to resume."
          buttonText="Resume"
          onButtonClick={handleResume}
        />
      ) : null}

      {/* Desktop Layout */}
      <div className={`row d-none d-md-flex`}>
        {leftOpen && (
          <div className={`col-md-3 ${styles.sidebar} position-relative p-0`}>
            <MarketFeed />
            <div
              className={`${styles.sidebarToggle} d-flex justify-content-center align-items-center`}
              onClick={() => setLeftOpen((prev) => !prev)}
            >
              <FaAngleLeft />
            </div>
          </div>
        )}

        <div
          className={`col-md-${
            leftOpen && rightOpen ? 6 : leftOpen || rightOpen ? 9 : 12
          } ${styles.chartArea} p-0`}
        >
          <AssetChart paused={inactive} />
          <OrderPanel
            mobile={
              (leftOpen && rightOpen && width <= 1360) ||
              ((leftOpen || rightOpen) && width <= 960)
            }
          />
        </div>

        {rightOpen && (
          <div className={`col-md-3 position-relative ${styles.summary}`}>
            <AccountSummary />
            <div
              className={`${styles.summaryToggle} d-flex justify-content-center align-items-center`}
              onClick={() => setRightOpen((prev) => !prev)}
            >
              <FaAngleRight />
            </div>
          </div>
        )}
      </div>

      {!leftOpen && (
        <div
          className={`${styles.closedSidebarToggle} d-flex justify-content-center align-items-center`}
          onClick={() => setLeftOpen((prev) => !prev)}
        >
          <FaAngleRight />
        </div>
      )}

      {!rightOpen && (
        <div
          className={`${styles.closedSummaryToggle} d-flex justify-content-center align-items-center`}
          onClick={() => setRightOpen((prev) => !prev)}
        >
          <FaAngleLeft />
        </div>
      )}

      {/* Mobile Layout */}
      <div className={`d-flex flex-column d-md-none ${styles.mobileLayout}`}>
        <div className={styles.mobileChartWrapper}>
          <AssetChart paused={shouldPause} />
        </div>
        <MarketFeed mobile />
        <AccountSummary compact />
      </div>
    </div>
  );
};

export default Arena;
